import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { Form, FormikProvider, useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import Div from "@jumbo/shared/Div";
import Loader from "@jumbo/components/Loader";
import { getBalanceSheetList } from "app/redux/slice/vendorSlice";

const formatINR = (amount) => {
  const num = Number(amount) || 0;
  const formatted = `₹${Math.abs(num).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
  return (
    <span style={{ color: num < 0 ? "red" : "green" }}>
      {num < 0 ? "- " : ""}
      {formatted}
    </span>
  );
};

const BalanceSheet = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    return today.getMonth() >= 3
      ? `${year}-${year + 1}`
      : `${year - 1}-${year}`;
  };

  const getLast10FinancialYears = () => {
    const today = new Date();
    const baseYear =
      today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return Array.from(
      { length: 10 },
      (_, i) => `${baseYear - i}-${baseYear - i + 1}`
    );
  };

  const financialYears = getLast10FinancialYears();

  const formik = useFormik({
    initialValues: {
      financialYear: getCurrentFinancialYear(),
    },
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      setSubmitting(true);
      const [startYear, endYear] = values.financialYear.split("-");
      const payload = {
        fromDate: `${startYear}-04-01`,
        toDate: `${endYear}-03-31`,
      };

      try {
        const response = await dispatch(getBalanceSheetList(payload)).unwrap();
        if (response.status === 200) {
          setData(response.data?.data?.result || {});
        } else {
          enqueueSnackbar(response.data?.response_message, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Failed to fetch balance sheet data", {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const { values, handleSubmit, isSubmitting, handleChange } = formik;

  useEffect(() => {
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { assets, liabilities, equity, summary, stockValue } = data;
  const cashAndBank = assets?.currentAssets?.cashAndBank || {};

  const renderRow = (label, amount, bold) => (
    <Box display="flex" justifyContent="space-between" mb={1}>
      <Typography fontWeight={bold ? 600 : 400}>{label}</Typography>
      <Typography fontWeight={600}>{formatINR(amount)}</Typography>
    </Box>
  );

  const sectionStyle = {
    border: "1px solid #e0e0e0",
    padding: "12px",
    marginBottom: "16px",
    borderRadius: "6px",
    backgroundColor: "#f9f9f9",
  };

  const totalBoxStyle = {
    backgroundColor: "#ff0055",
    color: "#ffffff",
    padding: "10px 16px",
    fontWeight: "bold",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "16px",
  };

  return (
    <>
      <Card sx={{ display: "flex", mb: 3 }}>
        <Div sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <CardContent>
            <FormikProvider value={formik}>
              <Form noValidate autoComplete="off">
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={10}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="financial-year-label">
                        Financial Year
                      </InputLabel>
                      <Select
                        labelId="financial-year-label"
                        name="financialYear"
                        value={values.financialYear}
                        onChange={handleChange}
                        label="Financial Year"
                      >
                        {financialYears.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <LoadingButton
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      loadingIndicator={<CircularProgress size={20} />}
                    >
                      Submit
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </Div>
      </Card>

      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={2}>
          {/* Assets */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              ASSETS
            </Typography>
            <Box sx={sectionStyle}>
              <Typography fontWeight="bold" mb={1}>
                Current Assets
              </Typography>
              {renderRow("Cash", cashAndBank.cash)}
              {renderRow("Bank", cashAndBank.bank)}
              <Divider sx={{ my: 1 }} />
              {renderRow("Total Cash & Bank", cashAndBank.total, true)}
              {renderRow(
                "Accounts Receivable",
                assets?.currentAssets?.accountsReceivable
              )}
              {/* {renderRow("Inventory", assets?.currentAssets?.inventory)} */}
              {renderRow("Goods In Stock", stockValue?.totalValue)}
              <Divider sx={{ my: 1 }} />
              {renderRow(
                "Total Current Assets",
                assets?.currentAssets?.total,
                true
              )}
            </Box>
            <Box sx={totalBoxStyle} style={{ marginTop: "58px" }}>
              <Typography>Total Assets</Typography>
              <Typography>
                ₹
                {(+assets?.totalAssets || 0).toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          </Grid>

          {/* Liabilities & Equity */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              LIABILITIES & EQUITY
            </Typography>
            <Box sx={sectionStyle}>
              <Typography fontWeight="bold" mb={1}>
                Current Liabilities
              </Typography>
              {renderRow(
                "Accounts Payable",
                liabilities?.currentLiabilities?.accountsPayable
              )}
              {renderRow(
                "Accrued Expenses",
                liabilities?.currentLiabilities?.accruedExpenses
              )}
              <Divider sx={{ my: 1 }} />
              {renderRow(
                "Total Current Liabilities",
                liabilities?.currentLiabilities?.total,
                true
              )}
            </Box>

            <Box sx={sectionStyle}>
              <Typography fontWeight="bold" mb={1}>
                Equity
              </Typography>
              {renderRow("Retained Earnings", equity?.retainedEarnings)}
              <Divider sx={{ my: 1 }} />
              {renderRow("Total Equity", equity?.totalEquity, true)}
            </Box>

            <Box sx={totalBoxStyle}>
              <Typography>Total Liabilities & Equity</Typography>
              <Typography>
                ₹
                {(+summary?.totalLiabilitiesAndEquity || 0).toLocaleString(
                  "en-IN",
                  {
                    maximumFractionDigits: 2,
                  }
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default BalanceSheet;
